import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  Button,
  Card,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Tooltip,
  Typography,
  Box,
  IconButton,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import ConfirmationDialog from "../../../dialogs/ConfirmationDialog";
import { CMSContext } from "../context/CaseManagmentContext";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import AddNewCaseModal from "../../../dialogs/AddNewCaseDialog";
import { updateCaseApi } from "../../../services/api/caseManagementApi";

const LawyerCard = ({
  title,
  data = [],
  onDelete = () => {},
  loading = false,
}) => {
  const { selectedCase, fetchCases } = useContext(CMSContext);

  const userLogin = useSelector((state) => state.userLogin);
  const { userInfo } = userLogin;
  const ownerId = userInfo._id;

  const caseAdvocate = selectedCase?.advocates?.find(
    (advocate) => advocate.userId === ownerId
  );

  const isOwner =
    selectedCase?.createdBy === ownerId || caseAdvocate?.role === "Owner";

  const [isEditing, setIsEditing] = useState(false);
  const [initialData, setInitialData] = useState([]);
  const [formData, setFormData] = useState(data);
  const [deleteModal, setDeleteModal] = useState(false);
  const [lawyerToDelete, setLawyerToDelete] = useState(null);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (data) {
      const updatedDetails = data.map((detail) => {
        const matchingRole = selectedCase?.advocates?.find(
          (advocate) => advocate.id === detail._id
        );
        return matchingRole ? { ...detail, role: matchingRole.role } : detail;
      });
      setFormData(updatedDetails);
      setInitialData(updatedDetails);
    }
    if (data.length === 0) {
      const updatedDetails =
        selectedCase?.lawyerDetails?.map((detail) => {
          const matchingRole = selectedCase?.advocates?.find(
            (advocate) => advocate.id === detail._id
          );
          return matchingRole ? { ...detail, role: matchingRole.role } : detail;
        }) || [];
      setFormData(updatedDetails);
      setInitialData(updatedDetails);
    }
  }, [data, selectedCase]);

  const handleEditClick = () => setIsEditing(true);
  const handleAddLawyer = () => {
    setOpenModal(true);
  };

  const handleSaveClick = async () => {
    setIsEditing(false);

    const editedLawyers = formData.filter((lawyer, index) => {
      return JSON.stringify(lawyer) !== JSON.stringify(initialData[index]);
    });

    if (editedLawyers.length > 0) {
      const updatedCaseLawyers = formData.map((lawyer) => ({
        id: lawyer._id,
        userId: lawyer.userId,
        role: lawyer.role,
      }));
      const updatedCase = {
        ...selectedCase,
        advocates: updatedCaseLawyers,
      };
      // Remove unwanted keys
      delete updatedCase.lawyerDetails;
      delete updatedCase.clientDetails;

      updateCase(updatedCase);
      //setSelectedCase(updatedCase);
    }
  };

  const handleChange = (index, key, value) => {
    setFormData((prev) =>
      prev.map((lawyer, i) =>
        i === index ? { ...lawyer, [key]: value } : lawyer
      )
    );
  };

  const handleDelete = (index) => {
    const lawyerHasTasks = selectedCase?.tasks?.some(
      (task) => task.assignedTo === formData[index].userId
    );

    if (lawyerHasTasks) {
      toast.error("Cannot delete lawyer with assigned tasks.");
      return;
    }
    const lawyerToDelete = formData[index];
    setLawyerToDelete(lawyerToDelete);
    setDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    const updatedLawyers = formData.filter(
      (lawyer) => lawyer._id !== lawyerToDelete._id
    );
    setFormData(updatedLawyers);

    // Remove the lawyer from advocates in selectedCase
    const updatedAdvocates = selectedCase?.advocates.filter(
      (advocate) => advocate.id !== lawyerToDelete._id
    );

    const updatedCase = {
      ...selectedCase,
      advocates: updatedAdvocates,
    };
    // Remove unwanted keys
    delete updatedCase.lawyerDetails;
    delete updatedCase.clientDetails;

    updateCase(updatedCase);
  };

  const updateCase = async (updates) => {
    try {
      const response = await updateCaseApi(selectedCase?._id, updates);

      if (response.status !== 200) {
        throw new Error("Failed to update the case");
      }

      const result = response.data;
      console.log(result.message);
      toast.success(result.message || "Case updated successfully");
      setDeleteModal(false);
      setIsEditing(false);
      fetchCases(true); // Refresh the cases after update
    } catch (error) {
      console.error("Error updating case:", error);
      // Handle error (e.g., show error message)
    }
  };

  return (
    <>
      <Card
        sx={{
          borderRadius: "10px",
          p: 1,
          display: "flex",
          flexDirection: "column",
          maxHeight: 390,
          minHeight: 390,
        }}
      >
        <Box
          sx={{
            bgcolor: "#003D78",
            color: "#FFFFFF",
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            p: "4px 8px 4px 8px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography fontSize={"15px"}>{title}</Typography>
          <Box sx={{ display: "flex", gap: "6px", alignItems: "center" }}>
            {isOwner && (
              <Tooltip title="Add Lawyer" arrow>
                <Button
                  size="small"
                  onClick={handleAddLawyer}
                  sx={{
                    bgcolor: "#FFFFFF",
                    minWidth: "25px",
                    minHeight: "25px",
                    padding: 0,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    boxShadow: 1,
                    "&:hover": { bgcolor: "#FFFFFF", cursor: "pointer" },
                  }}
                >
                  <FontAwesomeIcon
                    icon={faUserPlus}
                    style={{ fontSize: "14px", color: "#003D78" }}
                  />
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>
        <Box
          flex={1}
          sx={{
            overflowY: "auto",
            "::-webkit-scrollbar": { width: "5px" },
            "::-webkit-scrollbar-track": { background: "#fff" },
            "::-webkit-scrollbar-thumb": {
              background: "#bbb",
              borderRadius: "6px",
            },
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "300px",
              }}
            >
              <CircularProgress size={20} />
            </Box>
          ) : (
            <>
              {formData.length === 0 && (
                <Typography align="center" sx={{ fontSize: "13px", mt: 2 }}>
                  This case has no associated lawyers.{" "}
                </Typography>
              )}
              {formData.map((lawyer, index) => (
                <Box
                  key={lawyer._id || index}
                  sx={{
                    borderBottom: "1px solid #e0e0e0",
                    borderRadius: "4px",
                    p: "4px 8px 4px 8px",
                    mt: 2,
                    backgroundColor: "#f1f8ff",
                    boxShadow: 1,
                  }}
                >
                  {/* Lawyer Name */}
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      color="#003D78"
                      //gutterBottom
                    >
                      {lawyer.name || "N/A"}
                    </Typography>
                    {/* Delete Button for Owner */}
                    {/* {isEditing && (
                        <Button
                          onClick={() => {
                            handleDelete(index);
                          }}
                          size="small"
                          sx={{
                            ml: "auto",
                            color: "red",
                            textTransform: "none",
                            "& .MuiButton-startIcon": {
                              marginLeft: 0,
                              marginRight: 0,
                            },
                          }}
                          startIcon={
                            <FontAwesomeIcon
                              icon={faTrash}
                              style={{ fontSize: "12px" }}
                            />
                          }
                        ></Button>
                      )} */}
                  </Box>
                  {/* Lawyer Designation */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      mb: 1,
                    }}
                  >
                    {/* <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ width: "100px", fontSize: "13px" }}
                    >
                      Designation:
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: "13px" }}>
                      {lawyer.designation || "N/A"}
                    </Typography> */}
                  </Box>

                  {/* Role Dropdown */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      variant="body2"
                      fontWeight="bold"
                      sx={{ width: "100px", fontSize: "13px" }}
                    >
                      Role:
                    </Typography>
                    {isEditing ? (
                      <FormControl size="small">
                        <Select
                          sx={{
                            fontSize: "13px",
                            maxWidth: "10rem",
                            "& .MuiSelect-select": {
                              padding: "4px 8px",
                            },
                          }}
                          size="small"
                          value={lawyer.role || "Viewer"}
                          onChange={(e) =>
                            handleChange(index, "role", e.target.value)
                          }
                        >
                          <MenuItem sx={{ fontSize: "13px" }} value="Viewer">
                            Viewer
                          </MenuItem>
                          <MenuItem
                            sx={{ fontSize: "13px" }}
                            value="Contributor"
                          >
                            Contributor
                          </MenuItem>
                          <MenuItem sx={{ fontSize: "13px" }} value="Owner">
                            Owner
                          </MenuItem>
                        </Select>
                      </FormControl>
                    ) : (
                      <Typography variant="body2" sx={{ fontSize: "13px" }}>
                        {lawyer.role || "Viewer"}
                      </Typography>
                    )}
                  </Box>
                  {isOwner && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 1,
                        mt: 1,
                      }}
                    >
                      <Tooltip title={isEditing ? "Save" : "Edit"}>
                        <IconButton
                          onClick={() => {
                            if (isEditing) {
                              handleSaveClick();
                            } else {
                              handleEditClick();
                            }
                          }}
                          size="small"
                          sx={{
                            color: "#ffffff",
                            backgroundColor: "#003D78",
                            "&:hover": {
                              backgroundColor: "primary.main",
                            },
                            transition: "background-color 0.3s ease-in-out",
                            width: "24px",
                            height: "24px",
                            padding: "4px",
                          }}
                        >
                          {isEditing ? (
                            <SaveIcon sx={{ fontSize: "16px" }} />
                          ) : (
                            <EditIcon sx={{ fontSize: "16px" }} />
                          )}
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={isEditing ? "Cancel" : "Remove"}>
                        <IconButton
                          onClick={() => {
                            if (isEditing) {
                              setIsEditing(false);
                            } else {
                              handleDelete(index);
                            }
                          }}
                          size="small"
                          sx={{
                            color: "#ffffff",
                            backgroundColor: "#d32f2f",
                            "&:hover": {
                              backgroundColor: "#b71c1c",
                            },
                            width: "24px",
                            height: "24px",
                            padding: "4px",
                          }}
                        >
                          {isEditing ? (
                            <CancelIcon sx={{ fontSize: "16px" }} />
                          ) : (
                            <DeleteIcon sx={{ fontSize: "16px" }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
              ))}
            </>
          )}
        </Box>
        {openModal && (
          <AddNewCaseModal
            open={openModal}
            onClose={() => setOpenModal(false)}
            data={selectedCase}
            addNewLawyer={true}
          />
        )}
      </Card>
      {/* Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteModal}
        onClose={() => {
          setLawyerToDelete(null);
          setDeleteModal(false);
        }}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        contentText={`Are you sure you want to delete the lawyer "${lawyerToDelete?.name}" from this case?`}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        loading={loading}
      />
    </>
  );
};

export default LawyerCard;
